import { useCallback } from 'react';

import { log } from '@atlassian/jira-common-util-logging';
import { ff } from '@atlassian/jira-feature-flagging';
import { useEcClient as useEcClientOriginal } from '@atlassian/jira-jsis-ec-client';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import { PACKAGE_NAME } from '../../common/constants';
import type { CardMutation } from '../../common/types';

import type { CardsMutationHandler, Options } from './types';
import { toIssuesMutation } from './utils';

const defaultAnalyticsMetadata = { scenario: 'unknown', isCompanyManaged: false };

export type { CardsMutationHandler } from './types';

export const useEcClient = ({ analyticsMetadata = defaultAnalyticsMetadata }: Options) => {
	const { saveIssueMutationsToCache } = useEcClientOriginal();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const saveCardMutation = useCallback(
		(mutations: CardMutation[]) => {
			if (!ff('jsw.perf.jsis.integration')) {
				return;
			}

			try {
				const issuesMutations = toIssuesMutation(mutations);

				const analyticsDataKey = {
					analyticsEventObj: createAnalyticsEvent({}),
					analyticsMetadata: {
						synthetic: ff('jsw.pollinator.tenants'),
						isUIF: true,
						...analyticsMetadata,
					},
				};

				saveIssueMutationsToCache(issuesMutations, analyticsDataKey);
			} catch (error) {
				log.safeErrorWithoutCustomerData(
					PACKAGE_NAME,
					'JSIS EC client integration point unexpected error.',
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error as Error,
				);
			}
		},
		[analyticsMetadata, createAnalyticsEvent, saveIssueMutationsToCache],
	) as CardsMutationHandler;

	return { saveCardMutation };
};
